import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import { useRouter } from 'next/router';
import { useState } from 'react';

import { SankeyModal } from '@/components/Modal/SankeyModal';
import { TradeDetailsModal, TradeDetails } from '@/components/Modal/TradeDetailsModal';
import { TradeDetailsSelection } from '@/components/TradeHistoryTable/types';
import { getTradeDetailsRouteIdentity } from '@/components/TradeHistoryTable/utils';

import {
  ADVANCED_CLOSED_ORDER_STATUS_FILTER_OPTIONS,
  isAdvancedClosedOrderStatus,
  type AdvancedClosedOrderStatus,
} from '@/constants/advancedOrder';

import { useTradeHistoryRoute } from '@/hooks/useTradeHistoryRoute';
import type { LimitOrderFunders } from '@/hooks/useWalletOpenLimitOrders';

import { buttons } from '@/styles/buttons';
import { dialogCloseBtnStyles } from '@/styles/dialog';
import { tabsStyles } from '@/styles/tabs';
import { fontStyles } from '@/styles/typography';

import { Checkbox } from '@/ui/Checkbox';
import { XIcon } from '@/ui/Icons/XIcon';
import { Modal } from '@/ui/Modal';
import { Panel } from '@/ui/Panel';
import { Select } from '@/ui/Select';

import type { TokenResult } from '@/utils/0x/token-registry.types';
import { routes } from '@/utils/routes';

import { LimitCompletedTrades } from './LimitCompletedTrades';
import { LimitOpenOrdersTable } from './LimitOpenOrdersTable';

type LimitOrdersTab = 'open-orders' | 'completed';

type LimitOrdersPanelProps = {
  funders: LimitOrderFunders;
  /** The currently active trade pair, used to filter open orders. */
  sellToken: TokenResult | undefined;
  buyToken: TokenResult | undefined;
  className?: string;
};

const tabsClasses = tabsStyles({ variant: 'card' });

/**
 * Tabbed panel shown on the limit order page with the user's open limit orders
 * and their completed limit trades.
 */
export const LimitOrdersPanel = ({
  funders,
  sellToken,
  buyToken,
  className,
}: LimitOrdersPanelProps) => {
  const [currentTab, setCurrentTab] = useState<LimitOrdersTab>('open-orders');
  const [selectedTrade, setSelectedTradeDetails] = useState<TradeDetails | undefined>();
  const [isSankeyModalOpen, setIsSankeyModalOpen] = useState(false);
  const [filterThisPair, setFilterThisPair] = useState(false);
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [status, setStatus] = useState<AdvancedClosedOrderStatus | undefined>();

  const router = useRouter();

  const handleShowTradeDetails = (trade: TradeDetailsSelection) => {
    const routeIdentity = getTradeDetailsRouteIdentity(trade);
    if (!routeIdentity) return;
    router.push({
      pathname: routes.TX,
      query: {
        chainId: String(trade.chainId),
        txHash: routeIdentity.txHash,
        mode: 'advanced',
        ...(routeIdentity.funderAddress ? { funderAddress: routeIdentity.funderAddress } : {}),
      },
    });
  };

  const { data: routeData } = useTradeHistoryRoute({
    transactionHash: selectedTrade?.transactionHash,
    tradeId: selectedTrade?.tradeId,
    protocolVersion: selectedTrade?.protocolVersion,
  });

  return (
    <Panel className={className}>
      <Tabs.Root
        value={currentTab}
        onValueChange={(value) => setCurrentTab(value as LimitOrdersTab)}
      >
        <div className="flex min-h-[72px] items-center justify-between no-scrollbar px-7 py-4.5">
          <Tabs.List aria-label="view limit orders" className={tabsClasses.container()}>
            <Tabs.Trigger className={tabsClasses.tab()} value="open-orders">
              Open<span className="hidden tablet:inline"> orders</span>
            </Tabs.Trigger>
            <Tabs.Trigger className={tabsClasses.tab()} value="completed">
              <span className="hidden tablet:inline">Trade history</span>
              <span className="tablet:hidden">History</span>
            </Tabs.Trigger>
          </Tabs.List>

          <div className="hidden tablet:flex items-center gap-4">
            {currentTab === 'completed' && (
              <Select
                ariaLabel="Advanced order status"
                variant="secondary"
                items={ADVANCED_CLOSED_ORDER_STATUS_FILTER_OPTIONS}
                placeholderText="All statuses"
                selected={status ?? 'all'}
                onSelect={(value) =>
                  setStatus(isAdvancedClosedOrderStatus(value) ? value : undefined)
                }
                align="end"
              />
            )}
            <label
              className={fontStyles({
                variant: 'LABEL_MD',
                class: 'flex items-center gap-2 cursor-pointer',
              })}
            >
              <Checkbox
                checked={filterThisPair ? 'checked' : 'unchecked'}
                onCheckedChange={() => setFilterThisPair(!filterThisPair)}
              />
              Filter this pair
            </label>
          </div>

          <button
            className={buttons({
              size: 'small',
              shape: 'rounded',
              color: 'gray',
              class: ['tablet:hidden', 'py-2.5'],
            })}
            onClick={() => setIsMobileFiltersOpen(true)}
          >
            Filters
          </button>
        </div>
        <Tabs.Content value="open-orders">
          <LimitOpenOrdersTable
            funders={funders}
            setSelectedTradeDetails={setSelectedTradeDetails}
            pairFilter={filterThisPair ? { sellToken, buyToken } : undefined}
          />
        </Tabs.Content>
        <Tabs.Content value="completed">
          <LimitCompletedTrades
            funders={funders}
            onShowTradeDetails={handleShowTradeDetails}
            pairFilter={filterThisPair ? { sellToken, buyToken } : undefined}
            status={status}
          />
        </Tabs.Content>
      </Tabs.Root>

      <Modal
        isOpen={isMobileFiltersOpen}
        onOpenChange={setIsMobileFiltersOpen}
        onClose={() => setIsMobileFiltersOpen(false)}
      >
        <div className="p-6">
          <Dialog.Close className={dialogCloseBtnStyles({ absolute: true }).button()}>
            <XIcon aria-hidden className={dialogCloseBtnStyles().icon()} />
          </Dialog.Close>
          <Dialog.Title
            className={fontStyles({ variant: 'LABEL_MD', class: ['text-center', 'mt-1'] })}
          >
            Filters
          </Dialog.Title>
          <fieldset>
            <legend className={fontStyles({ variant: 'LABEL_LG', class: ['py-3'] })}>
              Limit orders
            </legend>
            <div className="flex items-center text-350">
              <Checkbox
                id="filter-this-pair"
                checked={filterThisPair ? 'checked' : 'unchecked'}
                onCheckedChange={() => setFilterThisPair(!filterThisPair)}
              />
              <label htmlFor="filter-this-pair" className="py-4 ml-3 w-full">
                Filter this pair
              </label>
            </div>
          </fieldset>
          {currentTab === 'completed' && (
            <fieldset>
              <legend className={fontStyles({ variant: 'LABEL_LG', class: ['py-3'] })}>
                Status
              </legend>
              <Select
                ariaLabel="Advanced order status"
                variant="secondary"
                items={ADVANCED_CLOSED_ORDER_STATUS_FILTER_OPTIONS}
                placeholderText="All statuses"
                selected={status ?? 'all'}
                onSelect={(value) =>
                  setStatus(isAdvancedClosedOrderStatus(value) ? value : undefined)
                }
                align="start"
                className="w-full justify-between"
              />
            </fieldset>
          )}
        </div>
      </Modal>

      {selectedTrade && (
        <TradeDetailsModal
          isOpen={!!selectedTrade}
          onClose={() => setSelectedTradeDetails(undefined)}
          onOpenChange={() => setSelectedTradeDetails(undefined)}
          trade={selectedTrade}
          route={routeData?.route}
          handleShowSankeyDiagram={() => setIsSankeyModalOpen(true)}
        />
      )}
      {selectedTrade && routeData?.route && (
        <SankeyModal
          route={routeData.route}
          chainId={Number(routeData.chainId)}
          isOpen={isSankeyModalOpen}
          onClose={() => setIsSankeyModalOpen(false)}
        />
      )}
    </Panel>
  );
};
